import React from 'react';
import PropTypes from 'prop-types';
import {
  Card, Column, Divider, Row, Typography,
} from '@tempmee/react';
import { EMPTY_VALUE } from '../../../../domain/Campaigns';
import { DetailStat } from './DetailStat';
import { PromoCodeStatusLabel } from './PromoCodeStatusLabel';

/**
 * The "Code Details" card, laid out as the Figma (Admin-Panel-v3, node
 * 2158:4146) has it: Total · Discount · Max Uses · Auto-apply, a rule, then
 * End Date · Start Date.
 *
 * "Total" is the campaign's redemption count. It comes from a second request
 * (usePromoCodeReport) that can fail on its own — it 404s for a non-discount
 * campaign, for one — so the card takes the report separately and shows an em
 * dash for that one figure rather than hiding the configuration with it.
 *
 * "Max Uses" is the per-office cap, the only cap nearly every code sets; the
 * campaign-wide cap is on the mapper but has no slot on the design.
 */
export const PromoCodeDetailsCard = ({ promoCode, report }) => (
  <Card outline className="flex-1 min-w-0">
    <Column px={5} py={4} gap={4}>
      <Row align="center" justify="between" gap={3}>
        <Typography variant="subtitle1" as="h2" bold className="text-text-secondary">
          Code Details
        </Typography>
        <PromoCodeStatusLabel status={promoCode.status} label={promoCode.statusLabel} />
      </Row>

      <Row gap={6} wrap="wrap">
        <DetailStat term="Total" value={report ? String(report.redemptions) : EMPTY_VALUE} />
        <DetailStat term="Discount" value={promoCode.discountLabel} />
        <DetailStat term="Max Uses" value={promoCode.maxUsesLabel} />
        <DetailStat term="Auto-apply" value={promoCode.autoApplyLabel} />
      </Row>

      <Divider />

      <Row gap={6} wrap="wrap">
        <DetailStat term="End Date" value={promoCode.endDateLabel} />
        <DetailStat term="Start Date" value={promoCode.startDateLabel} />
      </Row>
    </Column>
  </Card>
);

PromoCodeDetailsCard.propTypes = {
  promoCode: PropTypes.shape({
    status: PropTypes.string.isRequired,
    statusLabel: PropTypes.string.isRequired,
    discountLabel: PropTypes.string.isRequired,
    autoApplyLabel: PropTypes.string.isRequired,
    startDateLabel: PropTypes.string.isRequired,
    endDateLabel: PropTypes.string.isRequired,
    maxUsesLabel: PropTypes.string.isRequired,
  }).isRequired,
  /** The uptake report, or null while loading / when it failed. */
  report: PropTypes.shape({
    redemptions: PropTypes.number.isRequired,
  }),
};

PromoCodeDetailsCard.defaultProps = { report: null };

export default PromoCodeDetailsCard;
